import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

// ── Payload Types ─────────────────────────────────────────
export interface SeasonEntry {
  id: string;
  season_name: string;
  season_number: number;
  theme_id: string | null;
  start_date: string;
  end_date: string;
  is_active: boolean;
  is_completed: boolean;
  total_participants: number;
  total_xp_awarded: number;
  rewards_config: Record<string, never> | null;
}

export interface SeasonRankingEntry {
  user_id: string;
  rank: number;
  season_xp: number;
  season_level: number;
  username: string;
  full_name: string;
  avatar_url: string | null;
}

export interface SeasonListPayload {
  seasons: SeasonEntry[];
  total: number;
}

// ── Server Functions ──────────────────────────────────────
export const getSeasons = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
    })
  )
  .handler(async ({ data }) => {
    const { createAdminClient } = await import("./_admin-utils.server");
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError ?? "Missing server env vars" };

    const { data: { user }, error: authError } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { data: null, error: "Unauthorized" };

    const { data: seasons, error, count } = await client
      .from("gamification_seasons")
      .select("id, season_name, season_number, theme_id, start_date, end_date, is_active, is_completed, total_participants, total_xp_awarded, rewards_config", { count: "exact" })
      .order("season_number", { ascending: false });

    if (error) return { data: null, error: error.message };

    const entries: SeasonEntry[] = (seasons ?? []).map((s) => ({
      id: String(s.id),
      season_name: String(s.season_name),
      season_number: Number(s.season_number) || 0,
      theme_id: s.theme_id ? String(s.theme_id) : null,
      start_date: String(s.start_date),
      end_date: String(s.end_date),
      is_active: Boolean(s.is_active),
      is_completed: Boolean(s.is_completed),
      total_participants: Number(s.total_participants) || 0,
      total_xp_awarded: Number(s.total_xp_awarded) || 0,
      rewards_config: (s.rewards_config as Record<string, never>) ?? null,
    }));

    return { data: { seasons: entries, total: count ?? 0 }, error: null };
  });

export const getSeasonRankings = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      seasonId: z.string().uuid(),
      limit: z.number().int().min(1).max(100).default(20),
    })
  )
  .handler(async ({ data }) => {
    const { createAdminClient } = await import("./_admin-utils.server");
    const { client, error: initError } = await createAdminClient();
    if (!client) return { data: null, error: initError ?? "Missing server env vars" };

    const { data: { user }, error: authError } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { data: null, error: "Unauthorized" };

    const { data: rankings, error } = await client
      .from("season_rankings")
      .select("user_id, rank, season_xp, season_level, profiles(username, full_name, avatar_url)")
      .eq("season_id", data.seasonId)
      .order("rank", { ascending: true })
      .limit(data.limit);

    if (error) return { data: null, error: error.message };

    const entries: SeasonRankingEntry[] = (rankings ?? []).map((r) => {
      const profile = (r as Record<string, unknown>).profiles as Record<string, unknown> | null;
      return {
        user_id: String(r.user_id),
        rank: Number(r.rank) || 0,
        season_xp: Number(r.season_xp) || 0,
        season_level: Number(r.season_level) || 0,
        username: profile?.username ? String(profile.username) : "",
        full_name: profile?.full_name ? String(profile.full_name) : "",
        avatar_url: profile?.avatar_url ? String(profile.avatar_url) : null,
      };
    });

    return { data: entries, error: null };
  });
